// DoseTimingCard — surfaces the per-form PK context for the user's
// most recent dose (Gap 4 of the powder-tracking sprint).
//
// Reads the most recent dose from the dosesRes feed, computes its
// form-specific timeline via shared/pkConstants.ts, and renders:
//   - When the dose peaked (or will peak)
//   - When it's expected to clear
//   - The earliest plausible withdrawal-onset hour
//
// Why this exists: tablet 7-OH and kratom powder have dramatically
// different curves. A user transitioning from tablets to powder needs
// to know that what felt like "withdrawal in 4 hours" no longer
// applies — powder's longer mitragynine half-life means the felt
// window stretches to 12+ hours. Without this surface they'll redose
// from old expectations and over-shoot.
//
// Renders nothing when the most recent dose is >24 hours old (the
// timeline is irrelevant by then) or when no dose has been logged.

import { Clock } from 'lucide-react';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { KbLink } from './KbLink';
import { computeDoseTimeline, PK_BY_FORM } from '@shared/pkConstants';
import type { Dose } from '@shared/types';

interface DoseTimingCardProps {
  // Most recent dose, or null when none logged in the visible window.
  lastDose: Dose | null;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function fmtRelativeHours(hours: number): string {
  if (hours < 0) {
    const past = -hours;
    if (past < 1) return 'about now';
    return `${past < 10 ? past.toFixed(1) : Math.round(past)}h ago`;
  }
  if (hours < 1) return 'in under an hour';
  if (hours < 10) return `in ${hours.toFixed(1)}h`;
  return `in ${Math.round(hours)}h`;
}

export function DoseTimingCard({ lastDose }: DoseTimingCardProps) {
  if (!lastDose) return null;
  const ageHours = (Date.now() - new Date(lastDose.ts).getTime()) / 3_600_000;
  // After 24h the timeline is academic — the user has either redosed
  // or the dose is fully cleared. Hide so the card doesn't clutter
  // Today for a user who's been clean for a day+.
  if (ageHours > 24) return null;
  if (ageHours < 0) return null;

  const timeline = computeDoseTimeline(lastDose.ts, lastDose.form);
  const pk = PK_BY_FORM[lastDose.form];
  const peakInPast = Date.parse(timeline.peakAt) <= Date.now();
  const clearedInPast = Date.parse(timeline.clearedAt) <= Date.now();

  // Status text shifts based on where in the curve we currently are.
  let statusLine: string;
  if (clearedInPast) {
    statusLine = `Mostly cleared. ${pk.dominantAlkaloid} window has closed.`;
  } else if (peakInPast) {
    statusLine = `Past peak. ${pk.dominantAlkaloid} winding down through ${fmtTime(timeline.clearedAt)}.`;
  } else {
    statusLine = `Onset ${fmtTime(timeline.onsetAt)} · peak around ${fmtTime(timeline.peakAt)}.`;
  }

  const withdrawalRel = fmtRelativeHours(timeline.hoursUntilWithdrawalOnset);

  return (
    <Card accentLeft>
      <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--space-3)' }}>
        <Icon icon={Clock} size={18} />
        <div style={{ flex: 1, minInlineSize: 0 }}>
          <div style={{ fontWeight: 500, marginBlockEnd: 2 }}>Dose timing</div>
          <p className="muted text-caption" style={{ margin: 0, marginBlockEnd: 'var(--space-2)' }}>
            Last dose was at <strong>{fmtTime(lastDose.ts)}</strong>{' '}
            ({lastDose.amount} {lastDose.amountUnit} {lastDose.form.replace('_7oh', '').replace('_', ' ')}).
            {' '}{statusLine}
          </p>
          <p className="muted text-caption" style={{ margin: 0 }}>
            Earliest withdrawal-onset window opens{' '}
            <strong>{withdrawalRel}</strong>{' '}
            ({lastDose.form === 'leaf'
              ? 'powder runs long — mitragynine half-life is 43–68h, so the felt window stretches well past the peak'
              : lastDose.form === 'tablet_7oh'
                ? 'tablet 7-OH clears fast — ~3h half-life means the window opens soon'
                : 'extracts vary; this estimate skews toward mitragynine-dominant'}).
          </p>
          <p className="muted text-caption" style={{ margin: 0, marginBlockStart: 'var(--space-2)', fontSize: '11px' }}>
            Approximate based on typical PK. Your actual curve varies.{' '}
            <KbLink slug="leaf-potency-math">Learn more →</KbLink>
          </p>
        </div>
      </div>
    </Card>
  );
}
